import { useEffect } from "react";
import { TOOLTIP_CONTENT_CLASS } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
const GUARD = 8;
const GAP = 8;
const ARROW_INSET = 12;
const ARROW_TARGET = 24;
const DELAY = 200;
const TIP_CLASS = cn(TOOLTIP_CONTENT_CLASS, "pointer-events-none fixed left-0 top-0 z-[10001] w-max opacity-0 transition-opacity duration-[140ms]");
const ARROW_CLASS = "pointer-events-none absolute size-2 rotate-45 rounded-[1px] bg-tooltip";
/**
 * Bloom 2.0 Spec - Tooltips (reference: trunc-tip.js): one tooltip layer on <body> for
 * every element with `data-tip="…"`. Unlike the Radix tooltip it never touches the
 * element's ref, so it is safe on controls that re-render on every keystroke.
 * Above the element (below if there is no room), starts at its left edge, kept 8px
 * inside the viewport; the arrow points at the element's first 24px.
 */
export function DataTipLayer() {
    useEffect(() => {
        const tip = document.createElement("div");
        tip.setAttribute("role", "tooltip");
        tip.className = TIP_CLASS;
        const text = document.createElement("span");
        const arrow = document.createElement("span");
        arrow.className = ARROW_CLASS;
        tip.append(text, arrow);
        document.body.appendChild(tip);
        let current = null;
        let timer;
        const hide = () => {
            clearTimeout(timer);
            current === null || current === void 0 ? void 0 : current.removeAttribute("aria-describedby");
            current = null;
            tip.style.opacity = "0";
        };
        const show = (el) => {
            const label = el.getAttribute("data-tip");
            if (!label || !el.isConnected)
                return;
            current = el;
            text.textContent = label;
            tip.id || (tip.id = `bloom-tip-${Math.random().toString(36).slice(2)}`);
            el.setAttribute("aria-describedby", tip.id);
            const viewport = document.documentElement.clientWidth;
            const r = el.getBoundingClientRect();
            const t = tip.getBoundingClientRect();
            const above = r.top - GAP - t.height >= GUARD ||
                r.bottom + GAP + t.height > window.innerHeight - GUARD;
            const left = Math.max(GUARD, Math.min(r.left, viewport - GUARD - t.width));
            tip.style.left = `${left}px`;
            tip.style.top = `${above ? r.top - GAP - t.height : r.bottom + GAP}px`;
            const target = r.left + Math.min(r.width, ARROW_TARGET) / 2;
            const arrowX = Math.max(ARROW_INSET, Math.min(target - left - 4, t.width - ARROW_INSET - 8));
            arrow.style.left = `${arrowX}px`;
            arrow.style.top = above ? "auto" : "-4px";
            arrow.style.bottom = above ? "-4px" : "auto";
            tip.style.opacity = "1";
        };
        const onOver = (event) => {
            const el = event.target instanceof Element
                ? event.target.closest("[data-tip]")
                : null;
            if (el === current)
                return;
            hide();
            if (el)
                timer = setTimeout(() => show(el), DELAY);
        };
        const onOut = (event) => {
            if (!current) {
                clearTimeout(timer);
                return;
            }
            if (!current.contains(event.relatedTarget))
                hide();
        };
        document.addEventListener("mouseover", onOver, true);
        document.addEventListener("mouseout", onOut, true);
        document.addEventListener("scroll", hide, true);
        document.addEventListener("mousedown", hide, true);
        return () => {
            hide();
            document.removeEventListener("mouseover", onOver, true);
            document.removeEventListener("mouseout", onOut, true);
            document.removeEventListener("scroll", hide, true);
            document.removeEventListener("mousedown", hide, true);
            tip.remove();
        };
    }, []);
    return null;
}
